/**
 * Winess Collections Carousel — Web Component
 * --------------------------------------------------------------
 * Justified-row image packing (gallery style) with a height cap.
 *
 * All images in a slide share ONE common row height, chosen so the
 * whole group fits the slide width. The carousel-height setting is a
 * CAP — rows never exceed it. The whole image is always shown (never
 * cropped or stretched), and as many images as possible pack into each
 * slide.
 *
 *   - One image in a slide → fills the slide WIDTH (whole image, no crop);
 *     a wide banner takes up the whole slide (may exceed the cap).
 *   - Two+ wide images → shrink proportionally to split the width.
 *   - Many squares/portraits → pack several per slide at a shared height.
 *   - Leftovers that don't fill the width → centred at the cap height.
 *
 * The viewport height adapts to the active slide's row height (no wasted
 * vertical space) and animates between slides.
 *
 * Each image carries a per-image "max images in this slide" value (1 = solo,
 * fills the slide; higher = may share). A shared slide's capacity is the
 * smallest value among its images (most restrictive wins).
 *
 * Features: autoplay (pause on hover/focus/hidden/reduced-motion),
 * arrow buttons, dot indicators, pointer swipe, keyboard, resize
 * re-packing via ResizeObserver.
 */
if (!customElements.get('winess-collections-carousel')) {
  class WinessCollectionsCarousel extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.slideCount = 0;
      this.items = [];
      this.slideEls = [];
      this.slideHeights = [];
      this._autoplayTimer = null;
      this._pointerStartX = null;
      this._pointerDelta = 0;
      this._ro = null;
      this.cfg = {};
    }

    connectedCallback() {
      this.cfg = {
        carouselHeight: parseInt(this.dataset.carouselHeight, 10) || 400,
        gap: parseInt(this.dataset.gap, 10) || 8,
        autoplay: this.dataset.autoplay === 'true',
        autoplaySpeed: (parseFloat(this.dataset.autoplaySpeed) || 6) * 1000,
        showArrows: this.dataset.showArrows !== 'false',
        showDots: this.dataset.showDots !== 'false',
        reduceMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches
      };

      this.viewport = this.querySelector('.winess-collections-carousel__viewport');
      this.track = this.querySelector('.winess-collections-carousel__track');
      this.prevBtn = this.querySelector('.winess-collections-carousel__arrow--prev');
      this.nextBtn = this.querySelector('.winess-collections-carousel__arrow--next');
      this.dotsWrap = this.querySelector('.winess-collections-carousel__dots');

      // Capture original item nodes + aspect ratios (from Liquid data-attr).
      this.items = Array.from(this.querySelectorAll('.winess-collections-carousel__item'));
      this.items.forEach((it) => this._initItemAspect(it));

      this._bindEvents();
      this.build();

      // Re-pack when the container size changes.
      this._ro = new ResizeObserver(this._debounce(() => this.build(), 150));
      this._ro.observe(this.viewport);

      // If aspect was unknown, re-measure once the image loads.
      this.items.forEach((it) => {
        const img = it.querySelector('img');
        if (img && !img.complete) {
          img.addEventListener('load', () => {
            if (!parseFloat(it.dataset.aspectRatio)) {
              it.__aspect = (img.naturalWidth && img.naturalHeight)
                ? img.naturalWidth / img.naturalHeight
                : it.__aspect;
              this.build();
            }
          });
        }
      });

      this.maybeStartAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this._ro) this._ro.disconnect();
    }

    _initItemAspect(it) {
      it.__maxPerSlide = Math.max(1, parseInt(it.dataset.maxPerSlide, 10) || 4);
      const fromAttr = parseFloat(it.dataset.aspectRatio);
      if (fromAttr && fromAttr > 0) {
        it.__aspect = fromAttr;
        return;
      }
      const img = it.querySelector('img');
      it.__aspect = (img && img.naturalWidth && img.naturalHeight)
        ? img.naturalWidth / img.naturalHeight
        : 1.4;
    }

    _bindEvents() {
      this.prevBtn && this.prevBtn.addEventListener('click', () => this.prev());
      this.nextBtn && this.nextBtn.addEventListener('click', () => this.next());

      // Pointer swipe (mouse + touch)
      this.viewport.addEventListener('pointerdown', (e) => this._onPointerDown(e));
      this.viewport.addEventListener('pointermove', (e) => this._onPointerMove(e));
      this.viewport.addEventListener('pointerup', () => this._onPointerUp());
      this.viewport.addEventListener('pointercancel', () => { this._pointerStartX = null; });
      // Prevent image drag ghost interfering with swipe
      this.viewport.addEventListener('dragstart', (e) => e.preventDefault());

      // Keyboard
      this.setAttribute('tabindex', '0');
      this.addEventListener('keydown', (e) => this._onKey(e));

      // Autoplay pause/resume
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.maybeStartAutoplay());
      this.addEventListener('focusin', () => this.stopAutoplay());
      this.addEventListener('focusout', () => this.maybeStartAutoplay());
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopAutoplay(); else this.maybeStartAutoplay();
      });
    }

    _debounce(fn, ms) {
      let t;
      return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), ms); };
    }

    /* --------------------------------------------------------- *
     * Build: justified-row packing with a height cap.
     *   - All images in a slide share ONE row height, chosen so the
     *     whole group fits the slide width (classic gallery justify).
     *   - The carousel height (setting) is a CAP, never exceeded.
     *   - Whole image always shown; proportions kept; as many images
     *     as possible per slide.
     * --------------------------------------------------------- */
    build() {
      const slideWidth = this.viewport.clientWidth;
      if (slideWidth <= 0 || !this.items.length) return;

      // Height cap (shorter on mobile).
      const height = (slideWidth < 750)
        ? Math.round(this.cfg.carouselHeight * 0.6)
        : this.cfg.carouselHeight;
      const gap = this.cfg.gap;

      // Greedy: keep adding images to a slide until, at the cap height, they
      // would fill/exceed the slide width — then close the slide.
      //   closed slide  → row height = justifiedHeight (≤ cap), fills the width
      //   partial slide → row height = cap, centred (doesn't fill the width)
      const slides = [];
      let cur = [];
      let sumAspect = 0;
      let groupCap = Infinity;

      const closeSlide = () => {
        const netWidth = slideWidth - gap * (cur.length - 1);
        const jh = netWidth / sumAspect;
        // Dense row (fills at the cap) → justified height; sparse → cap height.
        const h = jh <= height ? jh : height;
        slides.push({ items: cur.slice(), h });
        cur = [];
        sumAspect = 0;
        groupCap = Infinity;
      };

      this.items.forEach((it) => {
        const m = it.__maxPerSlide;
        if (cur.length > 0) {
          // Can this image join the current slide? A slide's capacity is the
          // smallest max-per-slide among its images (most restrictive wins).
          if (cur.length + 1 > Math.min(groupCap, m)) closeSlide();
        }
        groupCap = cur.length === 0 ? m : Math.min(groupCap, m);
        cur.push(it);
        sumAspect += it.__aspect;

        // Close if the group is full or the row is full at the cap height.
        if (cur.length >= groupCap) { closeSlide(); return; }
        const netWidth = slideWidth - gap * (cur.length - 1);
        if (netWidth / sumAspect <= height) closeSlide();
      });
      if (cur.length) closeSlide();

      // A single image fills the slide WIDTH (whole image, no crop) — so a wide
      // banner takes up the whole slide. It may exceed the cap; sanity-cap very
      // tall (portrait) singles at 2× the cap so they don't dominate the page.
      slides.forEach((slide) => {
        if (slide.items.length === 1) {
          const it = slide.items[0];
          const fillH = slideWidth / it.__aspect;
          if (fillH <= height * 2) {
            slide.h = fillH;
            slide.fillWidth = true;
          } else {
            slide.h = height * 2;
          }
        }
      });

      // Rebuild DOM. this.items references keep the (detached) nodes alive.
      this.track.innerHTML = '';
      this.slideEls = [];
      this.slideHeights = [];

      slides.forEach((slide, sIdx) => {
        const slideEl = document.createElement('div');
        slideEl.className = 'winess-collections-carousel__slide';
        slideEl.setAttribute('role', 'group');
        slideEl.setAttribute('aria-roledescription', 'slide');
        slideEl.setAttribute('aria-label', `Slide ${sIdx + 1} of ${slides.length}`);
        slideEl.style.gap = gap + 'px';

        slide.items.forEach((it) => {
          it.style.width = slide.fillWidth
            ? slideWidth + 'px'
            : Math.round(slide.h * it.__aspect) + 'px';
          it.style.height = Math.round(slide.h) + 'px';
          slideEl.appendChild(it); // move original node
        });

        this.track.appendChild(slideEl);
        this.slideEls.push(slideEl);
        this.slideHeights.push(slide.h);
      });

      this.slideCount = this.slideEls.length;
      if (this.currentIndex > this.slideCount - 1) {
        this.currentIndex = Math.max(0, this.slideCount - 1);
      }

      this._renderControls();
      this.goTo(this.currentIndex, true);
      this.track.classList.add('is-ready');
    }

    _renderControls() {
      const multi = this.slideCount > 1;
      const showArrows = multi && this.cfg.showArrows;
      const showDots = multi && this.cfg.showDots;

      if (this.prevBtn) this.prevBtn.hidden = !showArrows;
      if (this.nextBtn) this.nextBtn.hidden = !showArrows;

      if (!this.dotsWrap) return;
      this.dotsWrap.hidden = !showDots;
      this.dotsWrap.innerHTML = '';
      if (!showDots) return;

      for (let i = 0; i < this.slideCount; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'winess-collections-carousel__dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.addEventListener('click', () => this.goTo(i));
        this.dotsWrap.appendChild(dot);
      }
    }

    /* --------------------------------------------------------- *
     * Navigation
     * --------------------------------------------------------- */
    goTo(idx, instant = false) {
      if (this.slideCount === 0) return;
      this.currentIndex = idx;
      if (this.cfg.reduceMotion) instant = true;

      this.track.style.transition = instant ? 'none' : '';
      this.track.style.transform = `translateX(${-idx * 100}%)`;
      if (instant) void this.track.offsetHeight; // force reflow

      this._updateDots();
      this._updateHeight(instant);

      this.slideEls.forEach((s, i) =>
        s.setAttribute('aria-hidden', i === idx ? 'false' : 'true')
      );
    }

    next() {
      if (this.slideCount === 0) return;
      if (this.currentIndex >= this.slideCount - 1) this.goTo(0, true); // wrap (cut)
      else this.goTo(this.currentIndex + 1);
    }

    prev() {
      if (this.slideCount === 0) return;
      if (this.currentIndex <= 0) this.goTo(this.slideCount - 1, true); // wrap (cut)
      else this.goTo(this.currentIndex - 1);
    }

    _updateDots() {
      if (!this.dotsWrap) return;
      this.dotsWrap.querySelectorAll('.winess-collections-carousel__dot').forEach((d, i) => {
        const active = i === this.currentIndex;
        d.classList.toggle('is-active', active);
        d.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    // Viewport height adapts to the active slide's row height (capped).
    _updateHeight(instant) {
      const h = this.slideHeights && this.slideHeights[this.currentIndex];
      if (!h) return;
      this.viewport.style.transition = instant ? 'none' : 'height 0.45s ease';
      this.viewport.style.height = Math.round(h) + 'px';
    }

    /* --------------------------------------------------------- *
     * Autoplay
     * --------------------------------------------------------- */
    maybeStartAutoplay() {
      this.stopAutoplay();
      if (!this.cfg.autoplay || this.cfg.reduceMotion) return;
      if (this.slideCount <= 1 || document.hidden) return;
      this._autoplayTimer = setInterval(() => this.next(), this.cfg.autoplaySpeed);
    }

    stopAutoplay() {
      if (this._autoplayTimer) {
        clearInterval(this._autoplayTimer);
        this._autoplayTimer = null;
      }
    }

    /* --------------------------------------------------------- *
     * Pointer / keyboard input
     * --------------------------------------------------------- */
    _onPointerDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this._pointerStartX = e.clientX;
      this._pointerDelta = 0;
    }

    _onPointerMove(e) {
      if (this._pointerStartX === null) return;
      this._pointerDelta = e.clientX - this._pointerStartX;
    }

    _onPointerUp() {
      if (this._pointerStartX === null) return;
      const dx = this._pointerDelta;
      this._pointerStartX = null;
      const threshold = 40;
      if (dx <= -threshold) this.next();
      else if (dx >= threshold) this.prev();
    }

    _onKey(e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.prev(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); this.next(); }
    }
  }

  customElements.define('winess-collections-carousel', WinessCollectionsCarousel);
}
